import { useState, type ReactNode } from 'react';
import {
  PLANS, PLAN_INCLUDES, PCN_DISCOUNT, CUSTOM_PRICING_THRESHOLD,
  PILOTS, PILOT_CREDIT_DAYS, FOUNDING_OFFER,
  SERVICES, SERVICES_BOUNDARY, money,
} from '@inbrief/shared';
import { Anchor } from './MarketingShell';

/**
 * A titled band of the page. Every one is a section with a heading, so the
 * document outline reads as a list of topics rather than a wall.
 *
 * `heading` exists because every band rendered an `h2` and the home page was
 * the only page on the site with an `h1`. Seven pages therefore began at level
 * two: no top-level heading for anyone navigating by heading, and nothing for
 * a search engine to read as the subject of the page. The automated audit
 * passes either way — `page-has-heading-one` is a best-practice rule rather
 * than a WCAG A/AA one — which is a fair illustration of why our own
 * accessibility statement says passing it is necessary and not sufficient.
 *
 * The first band on a page takes `heading="h1"`. The rest stay as they are.
 */
export function Band({ id, title, lede, tone, heading = 'h2', children }: {
  id?: string;
  title?: string;
  lede?: ReactNode;
  tone?: 'plain' | 'tint' | 'deep';
  heading?: 'h1' | 'h2';
  children: ReactNode;
}) {
  const Heading = heading;
  return (
    <section id={id} className={`mk-band mk-band-${tone ?? 'plain'}`}>
      <div className="mk-container">
        {title ? <Heading className="mk-band-title">{title}</Heading> : null}
        {lede ? <p className="mk-band-lede">{lede}</p> : null}
        {children}
      </div>
    </section>
  );
}

export function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <article className="mk-card">
      <h3 className="mk-card-title">{title}</h3>
      {children}
    </article>
  );
}

// ---------------------------------------------------------------------------
// The interactive demonstration
// ---------------------------------------------------------------------------

/**
 * Where the standalone demonstration lives. One place, so it cannot drift.
 *
 * Not `/demo/`, which is the marketing route describing it. They shared a
 * filename until the marketing pages started being prerendered, at which point
 * the build wrote one over the other and this button led back to the page it
 * was on.
 */
export const DEMO_URL = '/demo-app/index.html';

/**
 * The demonstration, opened rather than embedded.
 *
 * It was in an iframe, and an iframe was the wrong frame for it. The
 * demonstration is a full application layout — a sidebar, a header, ten
 * sections — and putting that inside a box on a marketing page gives it a
 * fraction of the screen, a scrollbar inside a scrollbar, and a keyboard trap
 * for anyone tabbing through the page. It also loads 400KB before a visitor
 * has said they want it.
 *
 * So: a still preview of the real interface, and a button that opens the whole
 * thing in its own tab at full size, with the marketing page still behind it.
 *
 * The authenticated application is never what opens. This is the standalone
 * file with fictional data and no network calls.
 */
export function DemoCallout({ heading = 'h2' }: { heading?: 'h2' | 'h3' }) {
  const Heading = heading;
  return (
    <div className="mk-demo">
      <div className="mk-demo-preview">
        <img
          src="/product-preview.jpg"
          srcSet="/product-preview-720.jpg 720w, /product-preview.jpg 1280w"
          sizes="(max-width: 860px) 100vw, 560px"
          alt="The InBrief contract breakdown, showing a service specification's obligations,
               indicators, deadlines and payment terms extracted into a summary, with the section
               navigation down the left."
          width={1280}
          height={800}
          loading="lazy"
        />
      </div>
      <div className="mk-demo-body">
        <Heading className="mk-demo-title">
          Interactive demonstration — fictional example data only
        </Heading>
        <p className="mk-demo-copy">
          The whole interface, at full size, in its own tab. Look through a contract breakdown,
          ask a question, draft a patient letter and translate one. No account, no sign-up,
          nothing to install.
        </p>
        <a
          className="btn btn-primary"
          href={DEMO_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          Open the interactive demo
          <span className="visually-hidden"> (opens in a new tab)</span>
        </a>
        <p className="mk-demo-note">
          No information is uploaded, stored, sent or retrieved from live systems in the
          demonstration. The practices and contracts in it are invented.
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The walkthrough
// ---------------------------------------------------------------------------

/**
 * Four screens, in the order someone uses them.
 *
 * This is where a thirty-second walkthrough video would go, and it is not one:
 * a video has to be recorded by a person with a voice, and a marketing page
 * that autoplays a silent screen recording is a video in name only. Four real
 * screenshots with a sentence each carry the same argument — this is a product
 * that exists, here is what using it looks like — without pretending.
 *
 * The images come from the approved standalone demonstration, so the data in
 * them is invented and no customer's contract can appear in a screenshot.
 * `apps/web/screenshots.cjs` regenerates them.
 */
const WALKTHROUGH = [
  {
    src: '/step-1-upload.jpg',
    small: '/step-1-upload-720.jpg',
    step: 'Upload',
    title: 'Put the specification in',
    copy: 'A contract, a service specification, or approved organisational information. It is read, segmented and checked before anything else happens.',
    alt: 'The InBrief documents screen, with an upload area and a list of processed contracts.',
  },
  {
    src: '/step-2-review.jpg',
    small: '/step-2-review-720.jpg',
    step: 'Review',
    title: 'See what it found, and where',
    copy: 'Obligations, indicators, deadlines, eligibility rules and payment terms, each one linked to the passage it came from.',
    alt: 'The InBrief contract breakdown, showing extracted obligations, indicators and payment terms for a contract.',
  },
  {
    src: '/step-3-ask.jpg',
    small: '/step-3-ask-720.jpg',
    step: 'Approve',
    title: 'A named person signs it off',
    copy: 'Approved items become what the organisation relies on. Anyone can then ask a question in plain English and see the source behind the answer.',
    alt: 'Ask InBrief, with a question box and common questions asked by frontline staff.',
  },
  {
    src: '/step-4-communicate.jpg',
    small: '/step-4-communicate-720.jpg',
    step: 'Use',
    title: 'Turn it into something a patient can read',
    copy: 'Letters, texts and leaflets drafted from approved content at a chosen reading level, with your practice details already in them.',
    alt: 'The patient communications screen, building a letter from approved service content with a live preview.',
  },
];

export function Walkthrough() {
  return (
    <ol className="mk-walk">
      {WALKTHROUGH.map((item, i) => (
        <li key={item.src} className="mk-walk-step">
          <div className="mk-walk-shot">
            <img
              src={item.src}
              srcSet={`${item.small} 720w, ${item.src} 1280w`}
              /* 342 CSS pixels on a phone, 563 on a desktop. Stated so the
                 browser can choose before it has laid the page out. */
              sizes="(max-width: 860px) calc(100vw - 48px), 563px"
              alt={item.alt}
              width={1280}
              height={800}
              loading="lazy"
            />
          </div>
          <div className="mk-walk-copy">
            <p className="mk-walk-label">
              <span className="mk-walk-n" aria-hidden="true">{i + 1}</span>
              {item.step}
            </p>
            <h3 className="mk-walk-title">{item.title}</h3>
            <p>{item.copy}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

// ---------------------------------------------------------------------------
// Trust
// ---------------------------------------------------------------------------

/**
 * The things a primary care buyer checks before they will take a meeting.
 *
 * Every line is a property of the software rather than an aspiration, and the
 * two that are neither — where the data is hosted, and what is in the DPA — are
 * pointed at the security page rather than summarised into a claim. A hosting
 * jurisdiction stated on a marketing page is the exact sentence somebody quotes
 * back during a DPIA.
 */
const TRUST = [
  {
    title: 'No patient-identifiable data',
    body: 'Not a policy — a refusal. Uploads are scanned and rejected if they look like they contain patient identifiers.',
  },
  {
    title: 'No clinical advice',
    body: 'No diagnosis, no triage, no treatment recommendations. That boundary is what keeps InBrief outside the medical device regime.',
  },
  {
    title: 'A source on every item',
    body: 'An item that cannot be traced back to a passage is discarded. The database will not store one without a citation.',
  },
  {
    title: 'A person approves',
    body: 'Nothing a patient would read gets there without someone with the right to approve it having done so, under their name.',
  },
  {
    title: 'History cannot be rewritten',
    body: 'Reviews, approvals and the audit log are append-only, enforced in the database rather than by convention.',
  },
  {
    title: 'One tenant cannot see another',
    body: 'Row-level security on every table holding customer content, forced, through a role that cannot bypass it.',
  },
  {
    title: 'Encrypted, and yours to delete',
    body: 'HTTPS throughout, encryption at rest, and a delete that removes every version and the stored files — not just the index.',
  },
  {
    title: 'Not used to train a model',
    body: 'Your documents go to the model that reads them, and are not training data for us or for the model provider.',
  },
];

export function TrustGrid() {
  return (
    <div className="mk-trust">
      {TRUST.map((item) => (
        <div key={item.title} className="mk-trust-item">
          <h3>{item.title}</h3>
          <p>{item.body}</p>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Proof
// ---------------------------------------------------------------------------

/**
 * Customer quotes, when there are customers to quote.
 *
 * The array is empty on purpose and the section renders something else while
 * it is. Placeholder testimonials were asked for, and a placeholder
 * testimonial on a live site is not a placeholder — it is a fabricated
 * endorsement, on a page whose entire argument is that this product does not
 * invent things it cannot cite. Adding a real one is a single object here, and
 * the layout is already written and waiting for it.
 */
interface Quote {
  quote: string;
  name: string;
  role: string;
  organisation: string;
}

const QUOTES: Quote[] = [];

export function Proof() {
  if (QUOTES.length > 0) {
    return (
      <div className="mk-quotes">
        {QUOTES.map((q) => (
          <figure key={q.name} className="mk-quote">
            <blockquote><p>{q.quote}</p></blockquote>
            <figcaption>
              <strong>{q.name}</strong>
              <span>{q.role}, {q.organisation}</span>
            </figcaption>
          </figure>
        ))}
      </div>
    );
  }

  return (
    <div className="mk-noquotes">
      <p className="mk-noquotes-lede">
        InBrief is early, and we are not going to put invented quotes on this page. Until there
        are pilot customers willing to be named, here is what you can check for yourself instead.
      </p>
      <div className="mk-three">
        <Card title="Use the whole product">
          <p>
            The interactive demonstration is the real interface with invented data — not a
            slideshow, not a lead-capture form. Open it and look for what it cannot do.
          </p>
          <Anchor to="/demo" className="btn btn-secondary">Open the demo</Anchor>
        </Card>
        <Card title="Test it on your own contract">
          <p>
            A demonstration takes half an hour and uses a specification you actually hold, so you
            see what it finds and what it misses rather than an example we chose.
          </p>
          <Anchor to="/contact" className="btn btn-secondary">Book one</Anchor>
        </Card>
        <Card title="Read how it handles your data">
          <p>
            Tenant separation, audit, retention, deletion and model usage, written for the person
            assembling a DPIA rather than for a brochure.
          </p>
          <Anchor to="/security" className="btn btn-secondary">Security and governance</Anchor>
        </Card>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The founder
// ---------------------------------------------------------------------------

/**
 * The founder's photograph, next to whatever is said about him.
 *
 * A real face is the cheapest credibility on a page like this and the hardest
 * for a competitor to copy — it is the difference between "an independent
 * software product" and a person who will be on the call. It is one image in
 * one place, so the home page and the About page cannot end up showing
 * different pictures or different captions.
 *
 * Not lazily loaded on the home page, where it sits mid-scroll and would
 * otherwise pop in as the reader reaches it; the file is 45KB.
 */
export function FounderPortrait() {
  return (
    <figure className="mk-portrait">
      <img
        src="/louis-williams.jpg"
        alt="Louis Williams, founder of InBrief."
        width={640}
        height={640}
      />
      <figcaption>
        <strong>Louis Williams</strong>
        <span>Founder, InBrief</span>
      </figcaption>
    </figure>
  );
}

// ---------------------------------------------------------------------------
// Pricing
// ---------------------------------------------------------------------------

const perMonthFromAnnual = (annualGbp: number) => Math.round(annualGbp / 12);

export function PricingTables({ heading = 'h3' }: { heading?: 'h2' | 'h3' }) {
  const [annual, setAnnual] = useState(true);
  const Heading = heading;

  const plans = [
    {
      plan: PLANS.core, includes: PLAN_INCLUDES.core,
      recommended: false, precedes: null as string | null,
    },
    {
      plan: PLANS.complete, includes: PLAN_INCLUDES.complete,
      recommended: true, precedes: `Everything in ${PLANS.core.name}, plus`,
    },
  ];

  return (
    <>
      <div className="mk-toggle" role="group" aria-label="Billing period">
        <button
          type="button"
          className={`mk-toggle-option${!annual ? ' is-on' : ''}`}
          aria-pressed={!annual}
          onClick={() => setAnnual(false)}
        >
          Monthly
        </button>
        <button
          type="button"
          className={`mk-toggle-option${annual ? ' is-on' : ''}`}
          aria-pressed={annual}
          onClick={() => setAnnual(true)}
        >
          Annual
        </button>
      </div>
      <p className="mk-toggle-note" aria-live="polite">
        {annual
          ? `Annual billing works out lower per month — Core is ${money(perMonthFromAnnual(PLANS.core.annualGbp))} a month rather than ${money(PLANS.core.monthlyGbp)}.`
          : 'Monthly billing, cancel at the end of any month. Annual is the lower effective monthly cost.'}
      </p>

      <div className="mk-plans">
        {plans.map(({ plan, includes, recommended, precedes }) => (
          <article key={plan.id} className={`mk-plan${recommended ? ' is-recommended' : ''}`}>
            {recommended ? <p className="mk-plan-flag">Recommended</p> : null}
            <Heading className="mk-plan-name">InBrief {plan.name}</Heading>
            <p className="mk-plan-price">
              <strong>{money(annual ? plan.annualGbp : plan.monthlyGbp)}</strong>
              <span> per practice / {annual ? 'year' : 'month'}</span>
            </p>
            <p className="mk-plan-alt">
              {annual
                ? `${money(perMonthFromAnnual(plan.annualGbp))} a month, billed annually`
                : `or ${money(plan.annualGbp)} per practice / year`}
            </p>
            {precedes ? <p className="mk-plan-precedes">{precedes}</p> : null}
            <ul className="mk-plan-list">
              {includes.map((item) => <li key={item}>{item}</li>)}
            </ul>
            <Anchor to="/contact" className="btn btn-primary mk-plan-cta">
              Choose {plan.name}
            </Anchor>
          </article>
        ))}
      </div>

      <p className="mk-vat">All prices exclude VAT.</p>

      <details className="mk-details">
        <summary>Buying as a PCN</summary>
        <div className="mk-details-body">
          <h4>Practices buying together</h4>
          <div className="table-wrap">
            <table className="dt">
              <caption className="visually-hidden">Discount by number of practices</caption>
              <thead>
                <tr><th scope="col">Practices</th><th scope="col">Discount</th></tr>
              </thead>
              <tbody>
                {Object.entries(PCN_DISCOUNT)
                  .filter(([count]) => Number(count) > 1)
                  .map(([count, pct]) => (
                    <tr key={count}><th scope="row">{count}</th><td>{pct}%</td></tr>
                  ))}
                <tr>
                  <th scope="row">{CUSTOM_PRICING_THRESHOLD} or more</th>
                  <td>Priced individually</td>
                </tr>
              </tbody>
            </table>
          </div>

        </div>
      </details>
    </>
  );
}

export function PilotOffer() {
  return (
    <div className="mk-two">
      <Card title="Paid pilot">
        <ul className="mk-list">
          {PILOTS.map((pilot) => (
            <li key={pilot.id}>
              <strong>{pilot.label}</strong>, {pilot.weeks} weeks — {money(pilot.priceGbp)}
            </li>
          ))}
        </ul>
        <p className="mk-small">
          The pilot fee is credited against an annual subscription if you convert
          within {PILOT_CREDIT_DAYS} days of it ending.
        </p>
      </Card>

      <Card title="Founding customer">
        <p className="mk-flag">Limited availability</p>
        <ul className="mk-list">
          <li><strong>{FOUNDING_OFFER.discountPct}% subscription discount</strong></li>
          {FOUNDING_OFFER.terms.map((term) => <li key={term}>{term}</li>)}
        </ul>
        <p className="mk-small">
          A fixed cohort rather than a standing offer. When it is full it closes.
        </p>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Services and boundaries
// ---------------------------------------------------------------------------

export function ServicesList() {
  return (
    <>
      <div className="note-box mk-boundary">{SERVICES_BOUNDARY}</div>
      <div className="mk-services">
        {SERVICES.map((service) => (
          <article key={service.id} className="mk-service">
            <h3 className="mk-service-name">{service.name}</h3>
            <p className="mk-service-summary">{service.summary}</p>
            <p className="mk-service-rate">{service.rate}</p>
          </article>
        ))}
      </div>
      <p className="mk-vat">All services are quoted per engagement and exclude VAT.</p>
    </>
  );
}

/**
 * What the product does not do.
 *
 * Every line is a real constraint enforced in the software, not a caveat added
 * for the website: there is no route that sends anything to a patient, and
 * every path to a model refuses patient-identifiable data. Saying so plainly
 * is the point — a buyer in primary care is right to ask, and a vague answer
 * is a worse answer.
 */
export function Boundaries() {
  return (
    <div className="mk-two">
      <Card title="What InBrief does">
        <ul className="mk-list">
          <li>Works only from documents your organisation has approved.</li>
          <li>Shows the passage behind every answer and every extracted item.</li>
          <li>Puts review and approval in front of anything a patient would read.</li>
          <li>Keeps an append-only record of who approved what, and when.</li>
        </ul>
      </Card>
      <Card title="What it does not do">
        <ul className="mk-list">
          <li>Send anything to a patient. There is no send route, and no messaging gateway.</li>
          <li>Give clinical advice, diagnose, or triage.</li>
          <li>Hold patient-identifiable data, or connect to a clinical system.</li>
          <li>Tell you which of your patients are outstanding on anything.</li>
          <li>Decide anything on your behalf. A person approves what the organisation relies on.</li>
        </ul>
      </Card>
    </div>
  );
}

/**
 * The questions a buyer asks before they will book anything.
 *
 * Every answer here is something the software actually does, checked against
 * the code rather than written from what would sell well. Two consequences of
 * that are worth keeping:
 *
 * The answer about practices says separate *organisations* are isolated and
 * that within one organisation everybody sees the same library. That is what
 * row-level security enforces — the policies key on organisation_id and
 * nothing else. "Yes, each practice sees only its own documents" would read
 * better and would be false.
 *
 * Hosting, the DPA, the subprocessor list and the medical-device position are
 * deliberately absent. They are real questions and they have real answers, but
 * those answers are commercial and legal commitments rather than properties of
 * the code, and inventing them on a website is how a procurement conversation
 * goes badly six weeks later. They point at contact instead.
 */
const FAQS: { q: string; a: ReactNode }[] = [
  {
    q: 'Can InBrief see our patient records?',
    a: <p>No. It has no connection to EMIS, SystmOne or any clinical system, and no
      integration is planned that would give it one. It reads the documents you upload and
      nothing else.</p>,
  },
  {
    q: 'Should we upload anything with patient details in it?',
    a: <p>No — and it tries not to let you. Every upload is scanned before it is processed,
      and one that appears to contain patient-identifiable data is rejected with the reason,
      rather than quietly accepted. Contracts and service specifications do not contain
      patient data, so this should never fire in normal use.</p>,
  },
  {
    q: 'Are our documents used to train an AI model?',
    a: <p>No. Your documents are sent to the model that reads them and are not used as
      training data by us or by the model provider.</p>,
  },
  {
    q: 'How do we know an answer is not invented?',
    a: <p>Every extracted item carries a citation to the passage it came from, and the
      interface shows you that passage. An item the system cannot trace back to the document
      is discarded rather than displayed — the database will not store one without a source.
      Answers to questions are drawn from the approved documents and quote them.</p>,
  },
  {
    q: 'What happens when a contract is reissued?',
    a: <p>Upload the new version against the same document. Previous versions stay readable,
      and each extraction stays attached to the version it was actually made from, so an item
      approved last year does not silently start claiming to describe this year's contract.</p>,
  },
  {
    q: 'Can different practices see different documents?',
    a: <p>Separate organisations cannot see each other's documents at all — that is enforced
      in the database rather than in application code, so a query that forgot to filter would
      still return nothing. Within one organisation, staff share the document library, and
      what differs is what each role may <em>do</em>: extract, draft communications, approve
      translations, manage settings.</p>,
  },
  {
    q: 'Can we control what staff can do?',
    a: <p>Yes. Eight roles, each with its own combination of four rights, enforced on the
      server rather than by hiding buttons. A receptionist can read the answers and cannot
      start an extraction or approve a communication.</p>,
  },
  {
    q: 'What can we upload?',
    a: <p>PDFs, up to 32MB each. A scanned PDF with no text layer is detected and reported
      rather than silently producing nothing.</p>,
  },
  {
    q: 'Does InBrief send anything to patients?',
    a: <p>No. It drafts communications for a named person to approve, and there is no send
      route and no messaging gateway in the product — approved text leaves as an export, and
      whatever you already use to contact patients sends it.</p>,
  },
  {
    q: 'Are translations checked by a person?',
    a: <p>A translation has to be approved by someone with the right to approve it before it
      can be used, and the approval is recorded against their name. Independent linguistic
      review by a qualified translator is available as a paid service where a communication
      warrants it.</p>,
  },
  {
    q: 'Can we get our documents back, and can they be deleted?',
    a: <p>Yes to both. Extracted items export to CSV, and deleting a document removes every
      version, its extractions and the stored files — which is what makes an erasure request
      actually erase something.</p>,
  },
  {
    q: 'Where is our data held, and can we see a DPA?',
    a: <p>Ask us and we will answer precisely, in writing, for the arrangement you would
      actually be on. We would rather tell you than publish something that stops being true
      when the infrastructure changes.</p>,
  },
];

export function Faq() {
  return (
    <div className="mk-faq">
      {FAQS.map((f) => (
        <details key={f.q}>
          <summary>{f.q}</summary>
          <div className="mk-faq-a">{f.a}</div>
        </details>
      ))}
    </div>
  );
}
